<template>
    <div id="FormParagraph" :class="{ active: isActive }" :style="paragraphMargins" @click="handleClick">
        <div class="form-paragraph">
            <!-- Quill Rich Text Editor -->
            <quill-editor
                ref="quillEditor"
                v-model="localContent"
                :options="editorOptions"
                @change="onEditorChange"
                @blur="onEditorBlur"
                class="paragraph-quill-editor"
            />
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.snow.css';

export default {
    name: "FormParagraph",
    components: {
        quillEditor
    },
    props: {
        item: {
            type: Object,
            default: () => ({})
        },
        isActive: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localContent: '',
            saveTimeout: null,
            isEditing: false,
            editorOptions: {
                theme: 'snow',
                placeholder: 'Buraya bilgilendirici metninizi yazabilirsiniz...',
                modules: {
                    toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ 'header': [1, 2, 3, false] }],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        ['clean']
                    ]
                }
            }
        };
    },
    computed: {
        paragraphContent() {
            return this.item.paragraph && this.item.paragraph.content 
                ? this.item.paragraph.content 
                : '<p>Buraya bilgilendirici metninizi yazabilirsiniz...</p>';
        },
        paragraphMargins() {
            const marginTop = this.item.paragraph && this.item.paragraph.marginTop || 0;
            const marginBottom = this.item.paragraph && this.item.paragraph.marginBottom || 0;
            return {
                marginTop: `${marginTop}px`,
                marginBottom: `${marginBottom}px`
            };
        }
    },
    watch: {
        paragraphContent: {
            immediate: true,
            handler(newContent) {
                // Sadece editing yapmıyorken update et
                if (!this.isEditing) {
                    this.localContent = newContent;
                }
            }
        }
    },
    methods: {
        ...mapActions({
            updateFormField: "forms/updateFormField",
        }),
        handleClick(e) {
            // Form controls butonları FormBuilder'da handle ediliyor
        },
        onEditorChange() {
            this.isEditing = true;
            this.debouncedSave();
        },
        onEditorBlur() {
            // Blur olduğunda kaydet ve editing'i bitir
            this.saveContent();
            this.isEditing = false;
        },
        debouncedSave() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            this.saveTimeout = setTimeout(() => {
                this.saveContent();
            }, 2000);
        },
        saveContent() {
            if (this.localContent !== this.paragraphContent) {
                const updateData = {
                    label: this.item.label || "Paragraf",
                    paragraph: {
                        ...this.item.paragraph,
                        content: this.localContent,
                    }
                };
                
                this.updateFormField({
                    siteId: this.$store.state.site.site._id,
                    formFieldId: this.item._id,
                    updateFields: updateData,
                });

                // Emit event to FormBuilder
                this.$emit('update-field', updateData);
                
                console.log('🟢 Paragraph content saved:', {
                    fieldId: this.item._id,
                    contentLength: this.localContent.length,
                    content: this.localContent.substring(0, 50) + '...'
                });
            } else {
                console.log('🟡 Paragraph content unchanged, not saving');
            }
        },
    },
    mounted() {
        // Initial content'i set et
        this.localContent = this.paragraphContent;
        console.log('🔵 Paragraph component mounted:', {
            fieldId: this.item._id,
            initialContent: this.paragraphContent.substring(0, 50) + '...',
            localContent: this.localContent.substring(0, 50) + '...'
        });
    },
    beforeDestroy() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
    }
};
</script>

<style lang="scss" scoped>
#FormParagraph {
    margin: 1rem 0;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 2px solid transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    position: relative;
    overflow: hidden;

    // Glassmorphism effect
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            rgba(255, 255, 255, 0.05) 100%);
        border-radius: inherit;
        pointer-events: none;
    }

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(242, 106, 82, 0.3);
        box-shadow: 
            0 8px 25px rgba(242, 106, 82, 0.1),
            0 4px 12px rgba(0, 0, 0, 0.05);
        background: rgba(255, 255, 255, 0.95);
    }

    &.active {
        border-color: #f26a52;
        background: rgba(242, 106, 82, 0.02);
        box-shadow: 
            0 0 0 3px rgba(242, 106, 82, 0.1),
            0 8px 25px rgba(242, 106, 82, 0.15);
        transform: translateY(-1px);

        &::before {
            background: linear-gradient(135deg, 
                rgba(242, 106, 82, 0.08) 0%, 
                rgba(242, 106, 82, 0.04) 100%);
        }

        .form-paragraph {
            .paragraph-content {
                color: #1f2937;
            }
        }
    }

    .form-paragraph {
        position: relative;
        z-index: 1;

        .paragraph-quill-editor {
            border-radius: 8px;
            overflow: hidden;
            border: 2px solid rgba(242, 106, 82, 0.3);
            background: white;
            transition: all 0.3s ease;

            &:hover {
                border-color: rgba(242, 106, 82, 0.5);
                box-shadow: 0 4px 12px rgba(242, 106, 82, 0.1);
            }

            // Quill toolbar özelleştirme
            :deep(.ql-toolbar) {
                border: none;
                border-bottom: 1px solid rgba(242, 106, 82, 0.1);
                background: rgba(248, 250, 252, 0.95);
                backdrop-filter: blur(10px);
                padding: 0.5rem;

                .ql-formats {
                    margin-right: 0.5rem;
                }

                .ql-picker-label,
                .ql-picker-item {
                    color: #374151 !important;
                }

                button {
                    &:hover {
                        color: #f26a52 !important;
                    }

                    &.ql-active {
                        color: #f26a52 !important;
                    }
                }

                .ql-stroke {
                    stroke: #6b7280;
                }

                .ql-fill {
                    fill: #6b7280;
                }

                button:hover .ql-stroke,
                button.ql-active .ql-stroke {
                    stroke: #f26a52;
                }

                button:hover .ql-fill,
                button.ql-active .ql-fill {
                    fill: #f26a52;
                }
            }

            // Quill editor container
            :deep(.ql-container) {
                border: none;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 0.875rem;
                line-height: 1.6;
                direction: ltr !important;

                .ql-editor {
                    min-height: 120px;
                    padding: 0.75rem;
                    direction: ltr !important;
                    text-align: left !important;
                    unicode-bidi: normal !important;

                    &.ql-blank::before {
                        color: #9ca3af !important;
                        font-style: italic;
                    }

                    p {
                        margin-bottom: 0.5rem;
                        direction: ltr !important;
                        text-align: left !important;
                    }

                    h1, h2, h3 {
                        direction: ltr !important;
                        text-align: left !important;
                        margin-bottom: 0.5rem;
                    }

                    ul, ol {
                        direction: ltr !important;
                        text-align: left !important;
                        margin-bottom: 0.5rem;
                    }
                }
            }
        }
    }

    // Responsive adjustments
    @media (max-width: 768px) {
        margin: 0.75rem 0;
        padding: 0.75rem;
    }

    @media (max-width: 480px) {
        margin: 0.5rem 0;
        padding: 0.625rem;
    }
}

// Performance optimizations
#FormParagraph {
    will-change: transform, box-shadow;
}

// Accessibility
@media (prefers-reduced-motion: reduce) {
    #FormParagraph {
        transition: none;
        
        &:hover {
            transform: none;
        }

        &.active {
            transform: none;
        }
    }
}
</style>
